import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, UserCheck, UserX, RefreshCw, ShieldAlert, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { logAdminAction } from "@/lib/adminLogger";

type AppRole = "admin" | "user" | "landlord" | "adjuster" | "client";

interface PendingUser {
  id: string;
  email: string;
  created_at: string;
  roles: AppRole[];
}

interface Props {
  onChange?: () => void;
}

export const PendingUsersList = ({ onChange }: Props) => {
  const [pending, setPending] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PendingUser | null>(null);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("id, email, created_at")
        .eq("is_disabled", true)
        .eq("disabled_reason", "Pending admin approval")
        .order("created_at", { ascending: false });
      if (error) throw error;

      const ids = (profiles ?? []).map((p) => p.id);
      let rolesByUser: Record<string, AppRole[]> = {};
      if (ids.length > 0) {
        const { data: roleRows } = await supabase
          .from("user_roles")
          .select("user_id, role")
          .in("user_id", ids);
        rolesByUser = (roleRows ?? []).reduce<Record<string, AppRole[]>>((acc, r) => {
          acc[r.user_id] = [...(acc[r.user_id] ?? []), r.role as AppRole];
          return acc;
        }, {});
      }

      setPending(
        (profiles ?? []).map((p) => ({
          ...p,
          roles: rolesByUser[p.id] ?? ["user"],
        }))
      );
    } catch (err) {
      console.error("Failed to load pending users:", err);
      toast.error("Failed to load pending users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (u: PendingUser) => {
    setBusyId(u.id);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ is_disabled: false, disabled_reason: null })
        .eq("id", u.id);
      if (error) throw error;

      await logAdminAction({ action: "user_approved", entity_type: "user", entity_id: u.id, details: { email: u.email } });

      // Notify the user in-app that they've been approved
      await supabase.from("notifications").insert({
        user_id: u.id,
        type: "role_change",
        title: "Account approved",
        message: "Your account has been approved by an administrator. You can now sign in.",
        metadata: { approved: true },
      });

      // Send approval email (fire-and-forget; don't block UI on email failures)
      try {
        const { error: emailError } = await supabase.functions.invoke("notify-user-approved", {
          body: { approvedUserId: u.id },
        });
        if (emailError) console.error("Approval email failed:", emailError);
      } catch (e) {
        console.error("Approval email invocation failed:", e);
      }

      toast.success(`Approved ${u.email}`);
      setPending((prev) => prev.filter((x) => x.id !== u.id));
      onChange?.();
    } catch (err: any) {
      console.error("Approve failed:", err);
      toast.error(err.message || "Failed to approve user");
    } finally {
      setBusyId(null);
    }
  };

  const openReject = (u: PendingUser) => {
    setRejectTarget(u);
    setConfirmEmail("");
  };

  const closeReject = () => {
    if (rejecting) return;
    setRejectTarget(null);
    setConfirmEmail("");
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    const u = rejectTarget;
    setRejecting(true);
    setBusyId(u.id);
    try {
      const { data, error } = await supabase.functions.invoke("delete-user", {
        body: { targetUserId: u.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      await logAdminAction({ action: "user_rejected", entity_type: "user", entity_id: u.id, details: { email: u.email } });
      toast.success(`Rejected ${u.email}`);
      setPending((prev) => prev.filter((x) => x.id !== u.id));
      setRejectTarget(null);
      setConfirmEmail("");
      onChange?.();
    } catch (err: any) {
      console.error("Reject failed:", err);
      toast.error(err.message || "Failed to reject user");
    } finally {
      setRejecting(false);
      setBusyId(null);
    }
  };

  const emailMatches =
    !!rejectTarget &&
    confirmEmail.trim().toLowerCase() === rejectTarget.email.trim().toLowerCase();

  return (
    <Card className="mb-6 border-primary/40">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-primary" /> Pending Users
            {pending.length > 0 && (
              <Badge variant="secondary" className="ml-2">{pending.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            New signups awaiting admin approval. Users cannot sign in until approved.
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} aria-label="Refresh pending users">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : pending.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No users awaiting approval.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Requested role</TableHead>
                <TableHead>Signed up</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pending.map((u) => {
                const isBusy = busyId === u.id;
                return (
                  <TableRow key={u.id}>
                    <TableCell className="font-medium">{u.email}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {u.roles.map((r) => (
                          <Badge key={r} variant="outline">{r}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{new Date(u.created_at).toLocaleString()}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        size="sm"
                        onClick={() => approve(u)}
                        disabled={isBusy}
                      >
                        {isBusy ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <UserCheck className="h-4 w-4 mr-1" /> Approve
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openReject(u)}
                        disabled={isBusy}
                      >
                        <UserX className="h-4 w-4 mr-1 text-destructive" /> Reject
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={!!rejectTarget} onOpenChange={(open) => { if (!open) closeReject(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Reject pending user
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 pt-2 text-sm text-muted-foreground">
                <p>
                  Rejecting will <strong className="text-foreground">permanently delete</strong> this
                  pending account. This action:
                </p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Removes the auth user, profile, and assigned roles.</li>
                  <li>Cancels their pending approval — they will not be notified.</li>
                  <li>Cannot be undone. They would need to sign up again.</li>
                </ul>
              </div>
            </DialogDescription>
          </DialogHeader>

          {rejectTarget && (
            <div className="space-y-3 py-2">
              <div className="rounded-md border bg-muted/50 px-3 py-2 text-sm">
                <span className="text-muted-foreground">User: </span>
                <span className="font-medium">{rejectTarget.email}</span>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-email">
                  Type <span className="font-mono text-foreground">{rejectTarget.email}</span> to confirm
                </Label>
                <Input
                  id="confirm-email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder={rejectTarget.email}
                  autoComplete="off"
                  disabled={rejecting}
                  maxLength={320}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeReject} disabled={rejecting}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmReject}
              disabled={!emailMatches || rejecting}
            >
              {rejecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" /> Rejecting…
                </>
              ) : (
                <>
                  <UserX className="h-4 w-4 mr-1" /> Reject &amp; delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
